import React, { Suspense, useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";

import { getAgentChatOpen, setAgentChatOpen } from "@plannel/redux/modules/AgentChatState";

const AgentChatPanelLazy = React.lazy(() => import("@plannel/components/agent/AgentChatPanel"));

/**
 * AgentChatHost — 앱 셸에 **1회만** 렌더되는 Agent Chat 패널 자리.
 *
 * 대화 자체는 여기 있지 않다 — `AgentChatEngineProvider`(App 루트)가 소유하고, 이 호스트는
 * **우측 패널 뷰의 표시/숨김**만 맡는다. 그래서:
 *   - 챗을 한 번도 열지 않아도 홈 AI 어시스턴트 카드가 같은 대화를 쓸 수 있다.
 *   - 최초로 열기 전까지 패널 청크를 받지 않는다(everOpened) — 초기 번들을 가볍게 유지.
 *   - 닫아도 언마운트하지 않는다(display 로만 숨긴다) — 스크롤·입력 초안이 살아남는다.
 *
 * 화면 컨텍스트(모듈·단계·어댑터 등)는 더 이상 여기서 중계하지 않는다. 엔진과 패널이
 * `useAgentChatScreen()` 으로 직접 읽는다(중계 prop 이 12개였고, 하나 빠뜨리면 조용히 죽었다).
 */
const AgentChatHost = ({ tabLabel, screenKey }) => {
  const dispatch = useDispatch();
  const open = useSelector(getAgentChatOpen);

  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => { if (open) setEverOpened(true); }, [open]);

  const handleClose = useCallback(() => dispatch(setAgentChatOpen(false)), [dispatch]);

  if (!everOpened) return null;

  const loading = (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: 280, height: "100%" }}>
      <CircularProgress size={28} />
    </Box>
  );

  return (
    // display 로만 숨긴다 — 여기서 언마운트하면 스크롤·입력 초안이 사라진다.
    <Box sx={{ display: open ? "flex" : "none", height: "100%", flexShrink: 0 }}>
      <Suspense fallback={loading}>
        <AgentChatPanelLazy open={open} onClose={handleClose} tabLabel={tabLabel} screenKey={screenKey} />
      </Suspense>
    </Box>
  );
};

export default AgentChatHost;
